'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import Link, { useLinkStatus } from 'next/link';
import { Loader2 } from 'lucide-react';
import { buttonVariant, ButtonIcon, getIconSize, getIconClass } from '.';
import type Button from '.';
import { cn } from '~/utils';

/**
 * Inner content of a {@link ButtonLink}. Reads the enclosing `<Link>`'s navigation state via
 * `useLinkStatus` so the button shows a spinner while a (potentially dynamic / slow) page is
 * loading — instant feedback on click instead of a frozen-feeling button.
 */
function ButtonLinkContent(t0) {
  const $ = _c(13);
  const {
    iconBefore,
    iconAfter,
    iconSize,
    iconClass,
    children
  } = t0;
  const {
    pending
  } = useLinkStatus();
  let t1;
  if ($[0] !== iconBefore || $[1] !== iconClass || $[2] !== iconSize || $[3] !== pending) {
    t1 = pending ? <Loader2 className={cn("mr-1 size-4 shrink-0 animate-spin", iconClass)} aria-hidden={true} /> : !!iconBefore && <ButtonIcon size={iconSize} name={iconBefore} className={["mr-1", iconClass]} />;
    $[0] = iconBefore;
    $[1] = iconClass;
    $[2] = iconSize;
    $[3] = pending;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== iconAfter || $[6] !== iconClass || $[7] !== iconSize) {
    t2 = !!iconAfter && <ButtonIcon size={iconSize} name={iconAfter} className={iconClass} />;
    $[5] = iconAfter;
    $[6] = iconClass;
    $[7] = iconSize;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== t1 || $[11] !== t2) {
    t3 = <>{t1}{children}{t2}</>;
    $[9] = children;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
export type ButtonLinkProps = Pick<React.ComponentProps<typeof Link>, 'children' | 'className' | 'href' | 'rel' | 'target' | 'onClick' | 'title' | 'scroll' | 'prefetch'> & Pick<React.ComponentProps<typeof Button>, 'variant' | 'size' | 'iconAfter' | 'iconBefore'> & {
  /** @default false */
  disabled?: boolean;
};
export default function ButtonLink(t0) {
  const $ = _c(38);
  let children;
  let className;
  let disabled;
  let href;
  let iconAfter;
  let iconBefore;
  let props;
  let rel;
  let t1;
  let t2;
  let target;
  if ($[0] !== t0) {
    ({
      href,
      target,
      rel,
      variant: t1,
      size: t2,
      iconAfter,
      iconBefore,
      className,
      children,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = href;
    $[5] = iconAfter;
    $[6] = iconBefore;
    $[7] = props;
    $[8] = rel;
    $[9] = t1;
    $[10] = t2;
    $[11] = target;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    href = $[4];
    iconAfter = $[5];
    iconBefore = $[6];
    props = $[7];
    rel = $[8];
    t1 = $[9];
    t2 = $[10];
    target = $[11];
  }
  const variant = t1 === undefined ? "tertiary" : t1;
  const size = t2 === undefined ? "md" : t2;
  let t3;
  if ($[12] !== size) {
    t3 = getIconSize(size);
    $[12] = size;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const iconSize = t3;
  let t4;
  if ($[14] !== variant) {
    t4 = getIconClass(variant);
    $[14] = variant;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const iconClass = t4;
  const t5 = disabled && "cursor-not-allowed! opacity-40";
  let t6;
  if ($[16] !== className || $[17] !== size || $[18] !== t5 || $[19] !== variant) {
    t6 = buttonVariant({
      size,
      variant,
      className: [className, t5]
    });
    $[16] = className;
    $[17] = size;
    $[18] = t5;
    $[19] = variant;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== disabled || $[22] !== props) {
    t7 = e => {
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      props?.onClick?.(e);
    };
    $[21] = disabled;
    $[22] = props;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== children || $[25] !== iconAfter || $[26] !== iconBefore || $[27] !== iconClass || $[28] !== iconSize) {
    t8 = <ButtonLinkContent iconBefore={iconBefore} iconAfter={iconAfter} iconSize={iconSize} iconClass={iconClass}>{children}</ButtonLinkContent>;
    $[24] = children;
    $[25] = iconAfter;
    $[26] = iconBefore;
    $[27] = iconClass;
    $[28] = iconSize;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== href || $[31] !== props || $[32] !== rel || $[33] !== t6 || $[34] !== t7 || $[35] !== t8 || $[36] !== target) {
    t9 = <Link {...props} className={t6} target={target} rel={rel} href={href} onClick={t7}>{t8}</Link>;
    $[30] = href;
    $[31] = props;
    $[32] = rel;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = target;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  return t9;
}